import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
    Alert,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import { handleDownloadTemplate } from '../../../../Utils/handleDownloadTemplate';
import { toast } from 'react-toastify';
import xtramJournalTemplate from '../../../../assests/templates/XtramJournalTemplate2.xlsx';

const XtramJournalImportModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    onSuccess,
    previewXtramJournal,
    importXtramJournal,
    downloadXtramJournalTemplate,
    t,
    direction = 'rtl',
}) => {
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const runPreview = useCallback(async () => {
        if (!file || !caseId || !accountantId || !token || !previewXtramJournal) return;
        try {
            setProcessing(true);
            const res = await previewXtramJournal(caseId, accountantId, file, token);
            setPreviewData({
                entries: res.entries ?? [],
                errorMessages: res.errorMessages ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                duplicateCount: res.duplicateCount ?? 0,
            });
            setPage(0);
        } catch (err) {
            console.error('Xtram journal preview failed:', err);
            setPreviewData({
                entries: [],
                errorMessages: [err?.message || 'Preview failed'],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
            });
        } finally {
            setProcessing(false);
        }
    }, [file, caseId, accountantId, token, previewXtramJournal]);

    useEffect(() => {
        if (open && file && caseId && accountantId && token && previewXtramJournal) {
            runPreview();
        } else if (!file) {
            setPreviewData(null);
        }
    }, [open, file, caseId, accountantId]);

    const handleDownloadTemplateClick = () => {
        handleDownloadTemplate(xtramJournalTemplate, 'XtramJournalTemplate2.xlsx');
    };

    const handleDrop = (files) => {
        if (files?.length) setFile(files[0]);
        setPreviewData(null);
    };

    const handlePreview = () => {
        runPreview();
    };

    const handleCommit = async () => {
        if (!file || !caseId || !token || !importXtramJournal) return;
        try {
            setProcessing(true);
            const res = await importXtramJournal(caseId, file, token, skipDuplicates);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const duplicatesSkipped = res?.duplicateCount ?? 0;
            const newCards = res?.newCardsCreated ?? 0;
            const messages = res?.errorMessages ?? [];
            const cardsSuffix = newCards > 0 ? ` | ${t('NewCardsCreated') || 'כרטיסים חדשים'}: ${newCards}` : '';
            const dupSuffix = duplicatesSkipped > 0
                ? ` | ${t('DuplicatesSkipped') || 'כפילויות שדולגו'}: ${duplicatesSkipped}`
                : '';
            if (errors > 0 && messages.length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}. ${messages.slice(0, 3).join(' ')}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}${cardsSuffix}${dupSuffix}`);
            } else {
                toast.success(processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}${cardsSuffix}${dupSuffix}` : t('ImportSuccessful'));
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Xtram journal import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData(null);
        setSkipDuplicates(true);
        setPage(0);
        setRowsPerPage(10);
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const entries = previewData?.entries ?? [];

    const importableCount = useMemo(
        () => entries.filter((e) => e.isValid && !(e.isDuplicate && skipDuplicates)).length,
        [entries, skipDuplicates]
    );

    const vatGroupedSummaries = useMemo(() => {
        const valid = entries.filter((e) => e.isValid && !(e.isDuplicate && skipDuplicates));
        const groups = {};
        valid.forEach((e) => {
            const vatKey = e.vatPercent != null ? Number(e.vatPercent) : 0;
            if (!groups[vatKey]) {
                groups[vatKey] = { vatPercent: vatKey, count: 0, totalAmount: 0 };
            }
            groups[vatKey].count += 1;
            groups[vatKey].totalAmount += Number(e.amount) || 0;
        });
        const sorted = Object.values(groups).sort((a, b) => a.vatPercent - b.vatPercent);
        const grandTotalAmount = valid.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const grandCount = valid.length;
        return { groups: sorted, grandTotalAmount, grandCount };
    }, [entries, skipDuplicates]);

    const newCardNames = useMemo(() => {
        const names = new Set();
        entries.forEach((e) => {
            if (e.originalCreditAccount && !e.creditAccount) names.add(e.originalCreditAccount);
            if (e.originalDebitAccount && !e.debitAccount) names.add(e.originalDebitAccount);
        });
        return [...names];
    }, [entries]);

    const resolvedCardNames = useMemo(() => {
        const items = [];
        const seen = new Set();
        entries.forEach((e) => {
            if (e.originalCreditAccount && e.creditAccount && !seen.has(e.originalCreditAccount)) {
                seen.add(e.originalCreditAccount);
                items.push({ name: e.originalCreditAccount, cardId: e.creditAccount });
            }
            if (e.originalDebitAccount && e.debitAccount && !seen.has(e.originalDebitAccount)) {
                seen.add(e.originalDebitAccount);
                items.push({ name: e.originalDebitAccount, cardId: e.debitAccount });
            }
        });
        return items;
    }, [entries]);

    if (!open) return null;

    const hasValidRows = importableCount > 0;
    const duplicateCount = previewData?.duplicateCount ?? 0;
    const newRowsToImport = importableCount;
    const paginatedEntries = entries.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{
                        position: 'relative',
                        maxWidth: previewData ? '1100px' : '560px',
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            {t('XtramJournal') || 'Xtram Journal'} – {t('Preview')} & {t('Import')}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <Button
                            variant="outlined"
                            onClick={handleDownloadTemplateClick}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            {t('DownloadXtramTemplate') || 'הורד תבנית'}
                        </Button>

                        <div>
                            <h5>.csv / .xlsx</h5>
                            <DropzoneArea
                                key={file ? file.name : 'xtram-empty'}
                                onDrop={handleDrop}
                                accept=".csv,.xlsx"
                            />
                            {file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {file.name}
                                </Typography>
                            )}
                        </div>

                        <Box display="flex" gap={1.5} mt={1}>
                            <Button
                                variant="contained"
                                onClick={handlePreview}
                                disabled={!file || processing}
                            >
                                {processing && !previewData ? t('Processing') : t('Preview')}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleCommit}
                                disabled={
                                    !previewData ||
                                    processing ||
                                    !hasValidRows
                                }
                            >
                                {processing && previewData ? t('Processing') : t('Import')}
                            </Button>
                        </Box>
                    </Box>

                    {previewData && (
                        <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('TotalLines') || 'סה"כ שורות'}: <strong>{previewData.totalLines}</strong>
                                {' | '}
                                {t('ValidCount') || 'תקינות'}: <strong>{previewData.validCount}</strong>
                                {' | '}
                                {t('ErrorCount') || 'שגיאות'}: <strong>{previewData.errorCount}</strong>
                                {duplicateCount > 0 && (
                                    <>
                                        {' | '}
                                        {t('DuplicateRows') || 'כפילויות'}: <strong>{duplicateCount}</strong>
                                    </>
                                )}
                                {' | '}
                                {t('NewRowsToImport') || 'שורות חדשות לייבוא'}: <strong>{newRowsToImport}</strong>
                            </Typography>

                            {duplicateCount > 0 && (
                                <Box mb={2}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={skipDuplicates}
                                                onChange={(e) => setSkipDuplicates(e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                {t('SkipDuplicateEntries') || 'דלג על רשומות כפולות בייבוא'} ({duplicateCount} {t('Duplicates') || 'כפילויות'})
                                            </Typography>
                                        }
                                    />
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                        {skipDuplicates
                                            ? (t('XtramDuplicatesSkipHint') || 'שורות שכבר קיימות במערכת לא ייובאו. בטל סימון כדי לייבא גם כפילויות.')
                                            : (t('XtramDuplicatesAddHint') || 'כפילויות מול המערכת ייובאו גם אם כבר קיימות פקודות זהות.')}
                                    </Typography>
                                </Box>
                            )}

                            {vatGroupedSummaries.groups.length > 0 && (
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxWidth: 500 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('vat') || 'מע"מ%'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Count') || 'כמות'}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('SumAmount') || 'סכום'}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {vatGroupedSummaries.groups.map((g) => (
                                                <TableRow key={g.vatPercent}>
                                                    <TableCell sx={{ fontSize: 12 }}>{g.vatPercent}%</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{g.count}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(g.totalAmount)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Total') || 'סה"כ'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{vatGroupedSummaries.grandCount}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{formatAmount(vatGroupedSummaries.grandTotalAmount)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {previewData.errorMessages?.length > 0 && (
                                <Box
                                    mb={2}
                                    p={1.5}
                                    bgcolor="error.light"
                                    borderRadius={1}
                                    sx={{ maxHeight: 220, overflow: 'auto' }}
                                >
                                    <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                        {t('Errors') || 'שגיאות'} ({previewData.errorMessages.length})
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {previewData.errorMessages.map((msg, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {msg}
                                                </Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {resolvedCardNames.length > 0 && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        {t('ResolvedAccounts') || 'חשבונות שזוהו'} ({resolvedCardNames.length})
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {resolvedCardNames.map((item, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span">
                                                    {item.name} → {t('CardId') || 'כרטיס'} {item.cardId}
                                                </Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Alert>
                            )}

                            {newCardNames.length > 0 && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        {t('NewCardsOnImport') || 'כרטיסי חשבון חדשים ייפתחו בייבוא'} ({newCardNames.length})
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {newCardNames.map((name, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span">
                                                    {name}
                                                </Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Alert>
                            )}

                            {entries.length > 0 && (
                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line') || 'שורה'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode') || 'קוד מיון'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CreditAccount') || 'חשבון זכות'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DebitAccount') || 'חשבון חובה'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Description')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Reference') || 'אסמכתא'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate') || 'תאריך'}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('vat') || 'מע"מ%'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status') || 'סטטוס'}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedEntries.map((row, i) => (
                                                <TableRow
                                                    key={row.lineNumber ?? i}
                                                    sx={{
                                                        bgcolor: row.isDuplicate
                                                            ? '#fff3e0'
                                                            : row.isValid
                                                                ? 'transparent'
                                                                : 'action.hover',
                                                        opacity: row.isDuplicate && skipDuplicates ? 0.55 : 1,
                                                        textDecoration: row.isDuplicate && skipDuplicates ? 'line-through' : 'none',
                                                    }}
                                                >
                                                    <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.sortCodeId ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>
                                                        {row.creditAccount ?? '—'}
                                                        {row.originalCreditAccount && (
                                                            <Typography variant="caption" display="block" color="info.main">
                                                                ({row.originalCreditAccount})
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>
                                                        {row.debitAccount ?? '—'}
                                                        {row.originalDebitAccount && (
                                                            <Typography variant="caption" display="block" color="info.main">
                                                                ({row.originalDebitAccount})
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.description}>
                                                        {row.description ?? ''}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.reference ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.documentDate ?? '—'}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.amount)}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.vatPercent != null ? row.vatPercent : '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {row.isDuplicate ? (
                                                            <Typography variant="body2" color="warning.main">
                                                                {t('Duplicate') || 'כפילות'}
                                                                {row.isValid && row.errorMessage && (
                                                                    <Typography variant="caption" display="block" color="info.main">
                                                                        {row.errorMessage}
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                        ) : row.isValid ? (
                                                            <>
                                                                <Typography variant="body2" color="success.main">{t('Valid') || 'תקין'}</Typography>
                                                                {row.errorMessage && (
                                                                    <Typography variant="caption" color="info.main">
                                                                        {row.errorMessage}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Typography variant="body2" color="error.main" title={row.errorMessage}>
                                                                {row.errorMessage ?? ''}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={entries.length}
                                        page={page}
                                        onPageChange={(_, p) => setPage(p)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        labelRowsPerPage={t('RowsPerPage') || 'שורות לעמוד'}
                                    />
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default XtramJournalImportModal;
